import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  boardDangerousCommentsPatchSchema,
  boardDangerousCommentsSchema,
  type ApiError,
  type BoardDangerousComments,
  type BoardDangerousCommentsPatch,
} from '@molt/shared';

import { patch } from './api';
import { queryKeys } from './query-keys';

/**
 * `PATCH /boards/:id/dangerous-comments` — the comment count at which a card
 * front turns red (`null` = off). Field-scoped (SC-4). It changes every card
 * front's tint, so the board's contents are refetched.
 */
export function updateBoardDangerousComments(
  boardId: string,
  body: BoardDangerousCommentsPatch,
): Promise<BoardDangerousComments> {
  return patch(
    `/boards/${encodeURIComponent(boardId)}/dangerous-comments`,
    boardDangerousCommentsSchema,
    boardDangerousCommentsPatchSchema.parse(body),
  );
}

export function useUpdateBoardDangerousComments(
  boardId: string,
): UseMutationResult<BoardDangerousComments, ApiError, BoardDangerousCommentsPatch> {
  const queryClient = useQueryClient();

  return useMutation<BoardDangerousComments, ApiError, BoardDangerousCommentsPatch>({
    mutationFn: (body) => updateBoardDangerousComments(boardId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.contents(boardId),
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.boards.detail(boardId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.cards.all() });
    },
  });
}
